import { useEffect, useMemo, useState } from "react";
import type { DeliveryPartner, DeliveryTask, DeliveryTaskStatus, DeliveryTimelineData } from "@silph-scope/shared";
import { useDashboard, usePanel } from "../api/hooks";
import { LoadPageHeader } from "./load/LoadShared";

const DASHBOARD_ID = "delivery-timeline";

const FILTERS: { id: FilterId; label: string }[] = [
  { id: "all", label: "All partners" },
  { id: "late", label: "Late" },
  { id: "blocked", label: "Blocked" }
];

type FilterId = "all" | "late" | "blocked";
type SortKey = "partner" | "deliverables" | "earliest" | "blocking" | "open" | "late" | "done" | "timeline" | "untyped";
type SortDir = "asc" | "desc";
type SortState = { key: SortKey; dir: SortDir };

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function DeliveryTimelinePage() {
  const { data: dashboard } = useDashboard(DASHBOARD_ID);
  const interval = dashboard?.refreshInterval ?? "1h";
  const panel = usePanel<DeliveryTimelineData>(DASHBOARD_ID, "partners", interval);
  const [filter, setFilter] = useState<FilterId>("all");
  const [openPartner, setOpenPartner] = useState<string | null>(null);
  const [missingOpen, setMissingOpen] = useState(false);

  const syncedAt = panel.data?.syncedAt ? new Date(panel.data.syncedAt) : null;
  const isStale = panel.data?.status === "stale" || panel.data?.status === "failed";
  const data = panel.data?.data;

  const activeRows = useMemo(
    () => (data?.rows ?? []).filter((row) => row.open > 0 || row.total === 0),
    [data]
  );
  const completedRows = useMemo(
    () => (data?.rows ?? []).filter((row) => row.open === 0 && row.total > 0),
    [data]
  );
  const visible = useMemo(() => activeRows.filter((row) => matches(filter, row)), [activeRows, filter]);

  return (
    <div className="task-ops">
      <LoadPageHeader
        dashboardId={DASHBOARD_ID}
        eyebrow="Operations"
        title="Delivery Timeline"
        subtitle="One row per partner. Dots run oldest to newest. Included types: MMR/Growth/Status, Platform Build, Dashboard - Add From Template, Filters - Custom, and Custom Dashboard."
        badges={["Rolling 12 months"]}
        extra={
          <button className="dt-missing-partner" type="button" onClick={() => setMissingOpen(true)} disabled={!data}>
            {data ? `${data.missingPartner?.length ?? 0} without a partner` : "Without a partner"}
          </button>
        }
        syncedAt={syncedAt}
        interval={interval}
        isFetching={panel.isFetching}
      />

      {isStale && (
        <div className="stale-banner">
          <i className="ri-error-warning-fill" />
          Data may be out of date — last sync check failed or is stale.
        </div>
      )}

      <div className="card table-panel">
        <div className="panel-header">
          <h2>Partners</h2>
          <span className="dt-filter-note">
            {data ? `${visible.length} partner${visible.length === 1 ? "" : "s"}` : "Loading"}
          </span>
        </div>

        <div className="dt-filters">
          {FILTERS.map((item) => (
            <button
              key={item.id}
              className={`dt-filter${filter === item.id ? " is-on" : ""}`}
              type="button"
              onClick={() => setFilter(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="dt-legend">
          <LegendItem kind="complete" label="Complete" />
          <LegendItem kind="open" label="Open" />
          <LegendItem kind="late" label="Overdue" />
          <LegendItem kind="blocked" label="Blocked" />
        </div>

        {panel.isError && <p className="dt-empty">Could not load partners.</p>}
        {panel.isLoading && <p className="dt-empty">Loading partners…</p>}
        {data && visible.length === 0 && (
          <p className="dt-empty">
            {filter === "all" ? "No partners have deliverables left." : "No partners match this filter."}
          </p>
        )}

        {data && visible.length > 0 && (
          <PartnerTable
            rows={visible}
            asOf={data.asOf}
            openPartner={openPartner}
            onToggle={setOpenPartner}
          />
        )}
      </div>

      {data && completedRows.length > 0 && (
        <div className="card table-panel">
          <div className="panel-header">
            <h2>Deliverables Completed</h2>
            <span className="dt-filter-note">
              {`${completedRows.length} partner${completedRows.length === 1 ? "" : "s"}`}
            </span>
          </div>
          <PartnerTable
            rows={completedRows}
            asOf={data.asOf}
            openPartner={openPartner}
            onToggle={setOpenPartner}
            defaultSort={{ key: "partner", dir: "asc" }}
          />
        </div>
      )}

      {missingOpen && data && (
        <MissingPartnerModal tasks={data.missingPartner} asOf={data.asOf} onClose={() => setMissingOpen(false)} />
      )}
    </div>
  );
}

function MissingPartnerModal({
  tasks,
  asOf,
  onClose
}: {
  tasks: DeliveryTask[];
  asOf: string;
  onClose: () => void;
}) {
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="cal-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="cal-modal dt-missing-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dt-missing-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="cal-modal-head">
          <div>
            <h2 id="dt-missing-title">No Parsec Partner</h2>
            <p className="dt-missing-copy">
              {tasks.length === 0
                ? "Every deliverable created in the rolling 12 months has a Parsec Partner."
                : `${tasks.length} task${tasks.length === 1 ? "" : "s"} created in the rolling 12 months ${tasks.length === 1 ? "has" : "have"} no Parsec Partner. Set one in Asana to include ${tasks.length === 1 ? "it" : "them"} on this timeline.`}
            </p>
          </div>
          <button className="icon-btn" type="button" title="Close" onClick={onClose}>
            <i className="ri-close-line" />
          </button>
        </div>
        {tasks.length > 0 && <DeliverableList tasks={tasks} asOf={asOf} />}
      </div>
    </div>
  );
}

function PartnerTable({
  rows,
  asOf,
  openPartner,
  onToggle,
  defaultSort = null
}: {
  rows: DeliveryPartner[];
  asOf: string;
  openPartner: string | null;
  onToggle: (partner: string | null) => void;
  defaultSort?: SortState | null;
}) {
  const [sort, setSort] = useState<SortState | null>(defaultSort);
  const ordered = useMemo(() => sortRows(rows, sort), [rows, sort]);

  function onSort(key: SortKey) {
    setSort((current) => {
      if (current?.key !== key) return { key, dir: defaultDir(key) };
      return { key, dir: current.dir === "asc" ? "desc" : "asc" };
    });
  }

  return (
    <div className="dt-board-scroll">
      <div className="dt-board-header">
        <SortHeader label="Partner" sortKey="partner" sort={sort} onSort={onSort} />
        <SortHeader
          label="Deliverables"
          sortKey="deliverables"
          sort={sort}
          onSort={onSort}
          sub="Oldest → newest"
        />
        <SortHeader label="Earliest" sortKey="earliest" sort={sort} onSort={onSort} />
        <SortHeader label="Blocking" sortKey="blocking" sort={sort} onSort={onSort} />
        <SortHeader label="Open" sortKey="open" sort={sort} onSort={onSort} align="right" />
        <SortHeader label="Late" sortKey="late" sort={sort} onSort={onSort} align="right" />
        <SortHeader label="Done" sortKey="done" sort={sort} onSort={onSort} align="right" />
        <SortHeader
          label="Timeline"
          sortKey="timeline"
          sort={sort}
          onSort={onSort}
          align="right"
          title="Platform Builds, from created to complete"
        />
        <SortHeader
          label="No type"
          sortKey="untyped"
          sort={sort}
          onSort={onSort}
          align="right"
          title="Open tasks with no Type"
        />
      </div>
      {ordered.map((row) => (
        <PartnerRow
          key={row.partner}
          row={row}
          asOf={asOf}
          open={openPartner === row.partner}
          onToggle={() => onToggle(openPartner === row.partner ? null : row.partner)}
        />
      ))}
    </div>
  );
}

function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
  align = "left",
  sub,
  title
}: {
  label: string;
  sortKey: SortKey;
  sort: SortState | null;
  onSort: (key: SortKey) => void;
  align?: "left" | "right";
  sub?: string;
  title?: string;
}) {
  const active = sort?.key === sortKey;
  const dir = active ? sort.dir : null;
  return (
    <button
      type="button"
      className={`dt-sort${align === "right" ? " is-right" : ""}${active ? " is-on" : ""}`}
      aria-sort={dir === "asc" ? "ascending" : dir === "desc" ? "descending" : "none"}
      title={title ?? `Sort by ${label}`}
      onClick={() => onSort(sortKey)}
    >
      <span>
        {label}
        {sub && <span className="dt-board-sub">{sub}</span>}
      </span>
      {active && (
        <i className={dir === "asc" ? "ri-arrow-up-line" : "ri-arrow-down-line"} aria-hidden="true" />
      )}
    </button>
  );
}

function defaultDir(key: SortKey): SortDir {
  return key === "partner" || key === "earliest" ? "asc" : "desc";
}

function sortRows(rows: DeliveryPartner[], sort: SortState | null): DeliveryPartner[] {
  if (!sort) return rows;
  const sign = sort.dir === "asc" ? 1 : -1;
  return rows.slice().sort((a, b) => {
    if (sort.key === "timeline") return compareTimeline(a, b, sign);
    const delta = compareSort(a, b, sort.key);
    if (delta !== 0) return delta * sign;
    return a.partner.localeCompare(b.partner);
  });
}

function compareTimeline(a: DeliveryPartner, b: DeliveryPartner, sign: number): number {
  if (a.timelineDays == null && b.timelineDays == null) return a.partner.localeCompare(b.partner);
  if (a.timelineDays == null) return 1;
  if (b.timelineDays == null) return -1;
  if (a.timelineDays !== b.timelineDays) return (a.timelineDays - b.timelineDays) * sign;
  return a.partner.localeCompare(b.partner);
}

function compareSort(a: DeliveryPartner, b: DeliveryPartner, key: SortKey): number {
  switch (key) {
    case "partner":
      return a.partner.localeCompare(b.partner);
    case "deliverables":
      return a.total - b.total;
    case "earliest":
      return a.earliestCreated.localeCompare(b.earliestCreated);
    case "blocking":
      return a.blocked - b.blocked;
    case "open":
      return a.open - b.open;
    case "late":
      return a.late - b.late;
    case "done":
      return doneRatio(a) - doneRatio(b) || a.completed - b.completed;
    case "timeline":
      return (a.timelineDays ?? 0) - (b.timelineDays ?? 0);
    case "untyped":
      return a.untypedOpen - b.untypedOpen;
  }
}

function doneRatio(row: DeliveryPartner): number {
  return row.total === 0 ? 0 : row.completed / row.total;
}

function matches(filter: FilterId, row: DeliveryPartner): boolean {
  if (filter === "late") return row.late > 0;
  if (filter === "blocked") return row.blocked > 0;
  return true;
}

function LegendItem({ kind, label }: { kind: DeliveryTaskStatus; label: string }) {
  return (
    <span className="dt-legend-item">
      <Dot kind={kind} />
      {label}
    </span>
  );
}

function Dot({ kind, title }: { kind: DeliveryTaskStatus; title?: string }) {
  return <span className={`dt-dot is-${kind}`} title={title} />;
}

function PartnerRow({
  row,
  asOf,
  open,
  onToggle
}: {
  row: DeliveryPartner;
  asOf: string;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div className={`dt-board-partner${open ? " is-open" : ""}`}>
      <button className="dt-board-row-main" type="button" onClick={onToggle} aria-expanded={open}>
        <span>
          <span className="dt-board-name">{row.partner}</span>
          <span className="dt-board-count">{`${row.total} deliverable${row.total === 1 ? "" : "s"}`}</span>
        </span>
        <span className="dt-seq">
          {row.tasks.map((task, index) => (
            <span key={task.id} className="dt-seq-item">
              {index > 0 && <span className="dt-seq-join" />}
              <Dot kind={task.status} title={dotTitle(task, asOf)} />
            </span>
          ))}
        </span>
        <span>{row.total > 0 ? formatDay(row.earliestCreated, asOf) : "—"}</span>
        <span className={row.blocked > 0 ? "dt-blocked-label" : "dt-mute"}>
          {row.blocked > 0 ? `${row.blocked} blocked` : "Clear"}
        </span>
        <span className="dt-num">{row.open}</span>
        <span className={`dt-num${row.late > 0 ? " is-late" : " is-quiet"}`}>{row.late}</span>
        <span className="dt-num is-strong">
          {row.completed}/{row.total}
        </span>
        <span className={timelineClass(row)} title={timelineTitle(row)}>
          {row.timelineDays == null ? "—" : formatTimeline(row.timelineDays)}
        </span>
        <span
          className={`dt-num${row.untypedOpen > 0 ? " is-untyped" : " is-quiet"}`}
          title="Open tasks with no Type. Set a Type in Asana to include them here."
        >
          {row.untypedOpen}
        </span>
      </button>
      {open && row.tasks.length > 0 && <DeliverableList tasks={row.tasks} asOf={asOf} />}
    </div>
  );
}

function DeliverableList({ tasks, asOf }: { tasks: DeliveryTask[]; asOf: string }) {
  return (
    <div className="dt-tasks">
      <div className="dt-detail-scroll">
        <div className="dt-task-header">
          <span />
          <span>Deliverable</span>
          <span>Type</span>
          <span>Created</span>
          <span>Due</span>
          <span>Status</span>
          <span>Assignee</span>
          <span />
        </div>
        {tasks.map((task) => (
          <div key={task.id} className="dt-task-row">
            <Dot kind={task.status} />
            <span className="task-title" title={task.title}>
              {task.title}
            </span>
            <span>{task.type}</span>
            <span className="mono">{formatDay(task.created, asOf)}</span>
            <span className="mono">{task.due ? formatDay(task.due, asOf) : "—"}</span>
            <span className={statusClass(task)}>{statusLabel(task, asOf)}</span>
            <span>{task.assignee}</span>
            <a className="dt-detail-link" href={task.url} target="_blank" rel="noopener noreferrer" title="Open in Asana">
              <i className="ri-external-link-line" />
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}

function dotTitle(task: DeliveryTask, asOf: string): string {
  return `${task.title} · ${task.type} · created ${formatDay(task.created, asOf)} · ${statusLabel(task, asOf)}`;
}

function statusLabel(task: DeliveryTask, asOf: string): string {
  if (task.status === "complete") return task.completedLate ? "Complete late" : "Complete";
  if (task.status === "blocked" && task.due != null && task.due < asOf) return "Blocked, late";
  if (task.status === "blocked") return "Blocked";
  if (task.status === "late") return "Late";
  return "Open";
}

function statusClass(task: DeliveryTask): string {
  if (task.status === "complete" && task.completedLate) return "dt-status is-late-done";
  if (task.status === "blocked") return "dt-status is-blocked";
  if (task.status === "late") return "dt-status is-late";
  return "dt-status";
}

function timelineClass(row: DeliveryPartner): string {
  if (row.timelineDays == null) return "dt-num is-quiet";
  if (row.timelineBlocked) return "dt-num is-blocked";
  if (row.timelineLate) return "dt-num is-late";
  if (row.timelineOpen) return "dt-num is-quiet";
  return "dt-num";
}

function timelineTitle(row: DeliveryPartner): string {
  if (row.timelineDays == null) return "No Platform Build tasks.";
  const pace = "Averaged from each Platform Build's created date to when it was marked complete.";
  if (row.timelineBlocked) return `${pace} At least one is blocked.`;
  if (row.timelineLate && row.timelineOpen) return `${pace} At least one is still open past its due date.`;
  if (row.timelineLate) return `${pace} At least one finished after its due date.`;
  if (row.timelineOpen) return `${pace} At least one is still open, counted through today.`;
  return `${pace} None finished after its due date.`;
}

function formatTimeline(days: number): string {
  if (days < 7) return days === 1 ? "1 day" : `${days} days`;
  const weeks = Math.round((days / 7) * 10) / 10;
  const label = Number.isInteger(weeks) ? String(weeks) : weeks.toFixed(1);
  return `${label} wk`;
}

function formatDay(iso: string, asOf: string): string {
  const [year, month, day] = iso.split("-");
  const monthNum = Number(month);
  const dayNum = Number(day);
  const monthLabel = MONTHS[monthNum - 1];
  if (!year || !monthLabel || !dayNum) return iso;
  const label = `${monthLabel} ${dayNum}`;
  return year === asOf.slice(0, 4) ? label : `${label}, ${year}`;
}
